// ViewPanel.jsx
// The VIEW ribbon tab. Groups all display/visibility controls in one place:
//   • Document   — Ruler, Formatting Marks (non-printable characters)
//   • Annotations — Comment highlights, Collaborator edit highlights
//   • Preview    — Book preview, Ebook preview (open read-only overlays)
import React from "react";
import { Box, Button, Divider, Switch, Typography } from "@mui/material";
import StraightenIcon from '@mui/icons-material/Straighten';
import NotesIcon from '@mui/icons-material/Notes';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import GroupIcon from '@mui/icons-material/Group';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import TabletAndroidIcon from '@mui/icons-material/TabletAndroid';

// Caption above each section group.
function SectionLabel({ children, sx }) {
  return (
    <Typography
      variant="caption"
      sx={{
        color: '#D4AF37',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        display: 'block',
        mb: 0.5,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

// A single labeled toggle row (icon + label on the left, Switch on the right).
function ToggleRow({ icon, label, checked, onChange, mt }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: '#f5f5f5',
        borderRadius: 2,
        px: 1.5,
        py: 0.5,
        ...(mt ? { mt: 0.5 } : {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>
      </Box>
      <Switch
        size="small"
        checked={!!checked}
        onChange={onChange}
        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
        sx={{
          '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#D4AF37' },
        }}
      />
    </Box>
  );
}

// A Preview action button (opens a read-only overlay).
function PreviewButton({ icon, label, onClick }) {
  return (
    <Button
      variant="contained"
      onClick={onClick}
      startIcon={icon}
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
      sx={{
        width: '100%',
        background: '#D4AF37',
        color: '#1A1A1A',
        fontWeight: 600,
        fontSize: '0.875rem',
        textTransform: 'uppercase',
        borderRadius: '8px',
        padding: '12px 24px',
        '&:hover': { background: '#F5D76E' },
      }}
    >
      {label}
    </Button>
  );
}

export default function ViewPanel({
  // Document
  showRuler,
  onToggleRuler,
  showFormattingMarks,
  onToggleFormattingMarks,
  // Annotations
  showCommentHighlights,
  onToggleCommentHighlights,
  showCollabEdits,
  onToggleCollabEdits,
  // How many live edits we've received this session; drives the empty-state hint.
  collabEditCount = 0,
  // Preview
  onOpenBookPreview,
  onOpenEbookPreview,
}) {
  const purpleIcon = { fontSize: 18, color: '#5A52A3' };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
      {/* ── Document section ── */}
      <Box>
        <SectionLabel>Document</SectionLabel>
        <ToggleRow
          icon={<StraightenIcon sx={purpleIcon} />}
          label="Ruler"
          checked={showRuler}
          onChange={onToggleRuler}
        />
        <ToggleRow
          icon={<NotesIcon sx={purpleIcon} />}
          label="Formatting Marks"
          checked={showFormattingMarks}
          onChange={onToggleFormattingMarks}
          mt
        />
      </Box>

      <Divider />

      {/* ── Annotations section ── */}
      <Box>
        <SectionLabel>Annotations</SectionLabel>
        <ToggleRow
          icon={<ChatBubbleOutlineIcon sx={purpleIcon} />}
          label="Comment Highlights"
          checked={showCommentHighlights}
          onChange={onToggleCommentHighlights}
        />
        <ToggleRow
          icon={<GroupIcon sx={purpleIcon} />}
          label="Collaborator Edits"
          checked={showCollabEdits}
          onChange={onToggleCollabEdits}
          mt
        />
        {/* Collaborator edits are tracked live over the socket and start empty
            on every load — they are not fetched from the server. With the
            toggle on and nobody typing there is genuinely nothing to paint,
            which is indistinguishable from the feature being broken. Say so. */}
        {showCollabEdits && collabEditCount === 0 && (
          <Typography
            sx={{
              fontSize: 11,
              lineHeight: 1.45,
              color: 'rgba(255,255,255,0.6)',
              mt: 0.75,
              px: 0.5,
            }}
          >
            No edits yet this session. Changes appear here as collaborators type.
          </Typography>
        )}
      </Box>

      <Divider />

      {/* ── Preview section ── */}
      <Box>
        <SectionLabel sx={{ mb: 1 }}>Preview</SectionLabel>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <PreviewButton
            icon={<MenuBookIcon />}
            label="Book Preview"
            onClick={onOpenBookPreview}
          />
          <PreviewButton
            icon={<TabletAndroidIcon />}
            label="Ebook Preview"
            onClick={onOpenEbookPreview}
          />
        </Box>
      </Box>

    </Box>
  );
}
